import CustomInput from 'components/CustomInput/CustomInput';
import { translate } from 'components/Translation';
import getEventValue from 'views/TopUpAddOns/helpers/getEventValue';
import Validator from 'card-validator';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { ClickAwayListener, InputAdornment } from '@material-ui/core';
import classNames from 'classnames';
import Close from '@material-ui/icons/Close';
import { Caption, HeadingS, HeadingSBold } from 'components/Typography/Typography';
import React, { useMemo, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/paymentFormStyle';
import americanExpress from 'assets/img/cards/american_express.svg';
import Mastercard from 'assets/img/cards/mastercard.svg';
import Visa from 'assets/img/cards/visa.svg';
import CardIcon from 'assets/img/cards/card.svg';
import SecurityCode from 'assets/img/security-code.svg';
import _ from 'lodash';
import Help from '@material-ui/icons/Help';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ICardInfoProps {
	errors: types.payment.IErrors;
	validation: types.payment.IValidation;
	paymentLimitReached: types.payment.paymentLimitReached;
	showCvvInfo: boolean;
	setShowCvvInfo: React.Dispatch<React.SetStateAction<boolean>>;
	formData: types.payment.ICardData;
	setFormData: (name: keyof types.payment.ICardData, value: string) => void;
	setValidation: React.Dispatch<React.SetStateAction<types.payment.IValidation>>;
}

const CardInfo = ({
	errors,
	validation,
	paymentLimitReached,
	showCvvInfo,
	setFormData,
	formData,
	setShowCvvInfo,
	setValidation,
}: ICardInfoProps) => {
	const classes = useStyles();

	const cardType = useMemo(() => {
		return _.get(validation.number, 'card.type');
	}, [validation.number]);
	useEffect(() => {
		setValidation((validation) => ({
			...validation,
			date: Validator.expirationDate(formData.date),
		}));
	}, [formData.date, setValidation]);

	useEffect(() => {
		setValidation((validation) => ({
			...validation,
			number: Validator.number(formData.number),
		}));
	}, [formData.number, setValidation]);

	useEffect(() => {
		setValidation((validation) => ({
			...validation,
			cvv: Validator.cvv(formData.cvv, _.get(validation.number, 'card.code.size', 3)),
		}));
	}, [validation.number, formData.cvv, setValidation]);

	const cardImage = useMemo(() => {
		switch (cardType) {
			case 'mastercard':
				return Mastercard;

			case 'visa':
				return Visa;

			case 'american-express':
				return americanExpress;

			default:
				return CardIcon;
		}
	}, [cardType]);

	const number = useMemo(() => {
		const gaps = _.get(validation.number, 'card.gaps');
		const number = formData.number.replace(/[^0-9]+/g, '');
		if (gaps) {
			let indexes = [[0, gaps[0]]];

			for (let i = 0; i < gaps.length - 1; ++i) {
				indexes.push([gaps[i], gaps[i + 1]]);
			}

			indexes.push([gaps[gaps.length - 1], 19]);

			return indexes
				.map(([indexA, indexB]) => number.substring(indexA, indexB))
				.filter((substr) => substr.length !== 0)
				.join(' ');
		}
		return number;
	}, [validation.number, formData.number]);

	return (
		<>
			<div style={{ position: 'relative' }}>
				{<img className={classes.cardBrand} src={cardImage} alt="card" />}
				<CustomInput
					formControlProps={{
						fullWidth: true,
					}}
					error={errors.number}
					helperText={
						errors.number &&
						translate(
							`payments.topup.brand.${['mastercard', 'visa'].includes(cardType) ? cardType : ''}Error`
						)
					}
					labelText={translate('payments.topup.cardNumber')}
					id="payment-card-number"
					inputProps={{
						value: number,
						onChange: (event: types.topUp.FormEvent) => {
							const value = getEventValue(event).replace(/[ ]+/g, '');
							if (/^[0-9]{0,16}$/.test(value)) {
								setFormData('number', value);
							}
						},
					}}
				/>
			</div>
			<CustomInput
				formControlProps={{
					fullWidth: true,
				}}
				labelText={translate('payments.topup.cardHolder')}
				id="payment-holder"
				inputProps={{
					value: formData.holder,
					onChange: (event: types.topUp.FormEvent) => {
						const value = getEventValue(event);
						if (/^[a-zA-Z ]*$/.test(value)) {
							setFormData('holder', value);
						}
					},
				}}
			/>
			<div className={classes.monthCvvContainer}>
				<div>
					<CustomInput
						id="expDate"
						formControlProps={{
							fullWidth: true,
						}}
						error={errors.date}
						helperText={errors.date && translate('payments.topup.wrongDate')}
						labelText={translate('payments.topup.expDate')}
						inputProps={{
							value: formData.date.length === 0 ? '' : formData.date.match(/.{1,2}/g)!.join('/'),
							onChange: (event: types.topUp.FormEvent) => {
								const value = getEventValue(event).replace(/[ /]+/g, '');
								if (/^[0-9]{0,4}$/.test(value)) {
									setFormData('date', value);
								}
							},
							placeholder: 'MM/YY',
						}}
					/>
				</div>
				<div>
					<ConditionalRender show={showCvvInfo}>
						<ClickAwayListener onClickAway={() => setShowCvvInfo(false)}>
							<div className={classes.cvvInfo}>
								<div className={classNames(classes.right, classes.close)}>
									<Close onClick={() => setShowCvvInfo(false)} />
								</div>
								<div>
									<HeadingS inline>{translate('payments.topup.cvv1')}</HeadingS>
									{cardType === 'american-express' ? (
										<HeadingSBold inline>{translate('payments.topup.cvv2.amex')}</HeadingSBold>
									) : (
										<HeadingSBold inline>{translate('payments.topup.cvv2')}</HeadingSBold>
									)}
									<HeadingS inline>{translate('payments.topup.cvv3')}</HeadingS>
								</div>
								<div className={classNames(classes.center, classes.securityCode)}>
									<img src={SecurityCode} alt="securitycode" />
								</div>
							</div>
						</ClickAwayListener>
					</ConditionalRender>
					<CustomInput
						id="cvv"
						formControlProps={{ fullWidth: true }}
						error={errors.cvv}
						helperText={errors.cvv && translate('payments.topup.wrongCVV')}
						labelText={translate('payments.topup.securityCode')}
						inputProps={{
							value: formData.cvv,
							type: 'password',
							endAdornment: (
								<InputAdornment position="end" id="payment-help">
									<Help className={classes.help} onClick={() => setShowCvvInfo(true)} />
								</InputAdornment>
							),
							onChange: (event: types.topUp.FormEvent) => {
								const value = getEventValue(event);
								if (/^[0-9]{0,4}$/.test(value)) {
									setFormData('cvv', value);
								}
							},
						}}
					/>
				</div>
			</div>
			<ConditionalRender show={!!paymentLimitReached}>
				<div className={classes.limitWarning}>
					<Caption color="danger">
						{translate(`payments.topup.payment.error.youReachedLimit.${paymentLimitReached}`)}
					</Caption>
					<Caption disableMargin color="danger">
						{translate(`payments.topup.payment.error.reduce.${paymentLimitReached}`)}
					</Caption>
				</div>
			</ConditionalRender>
		</>
	);
};

export default CardInfo;
